import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ResearcherCard from '@/features/researcher/components/ResearcherCard';
import Paginator from '@/features/shared/ui/Paginator';
import PublicationCard from '@/features/publication/components/PublicationCard';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

const PROFESSOR_NAMES = ['교수', '기금교수'];
const GRADUATE_NAME = '대학원생';

function isProfessor(s) {
  const name = (s?.sinbun_name || '').trim();
  return PROFESSOR_NAMES.includes(name);
}
function isGraduate(s) {
  return (s?.sinbun_name || '').trim() === GRADUATE_NAME;
}
function isMoreScholar(s) {
  const name = (s?.sinbun_name || '').trim();
  return name && !PROFESSOR_NAMES.includes(name) && name !== GRADUATE_NAME;
}

export default function DepartmentTabsContent({
  allScholars = [],
  scholarsListRef,
  deptId,
  perPage = 9,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('researchers');
  const [sinbunFilter, setSinbunFilter] = useState('professor'); // professor | graduate | more
  const [currentPage, setCurrentPage] = useState(1);
  const isResearchers = tab === 'researchers';
  const isResources = tab === 'resources';
  const tabBtnCls = 'h-[52px] px-6 cursor-pointer';

  const filteredScholars = useMemo(() => {
    if (!Array.isArray(allScholars)) return [];
    const workingOnly = allScholars.filter((s) => (String(s?.is_working || '').toLowerCase() === 'y'));
    if (sinbunFilter === 'professor') return workingOnly.filter(isProfessor);
    if (sinbunFilter === 'graduate') return workingOnly.filter(isGraduate);
    if (sinbunFilter === 'more') return workingOnly.filter(isMoreScholar);
    return [];
  }, [allScholars, sinbunFilter]);

  const totalFiltered = filteredScholars.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const paginatedScholars = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredScholars.slice(start, start + perPage);
  }, [filteredScholars, currentPage, perPage]);

  const pageInfo = useMemo(() => ({
    totalPage: totalPages,
    fromPage: currentPage,
    totalResults: totalFiltered,
    resultsPerPage: perPage,
  }), [totalPages, currentPage, totalFiltered, perPage]);

  const onPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    try {
      if (scholarsListRef?.current && typeof window !== 'undefined') {
        const el = scholarsListRef.current;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 280;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } catch (e) {}
  };

  const handleSinbunFilter = (value) => {
    setSinbunFilter(value);
    setCurrentPage(1);
  };

  // Resources (publications) 데이터 관리
  const [publications, setPublications] = useState([]);
  const [publicationsLoading, setPublicationsLoading] = useState(false);
  const [publicationsError, setPublicationsError] = useState(null);
  const [showAllPublications, setShowAllPublications] = useState(false);

  // Resources 탭에서 publications API 호출 (한 번만)
  useEffect(() => {
    if (tab !== 'resources' || !deptId || publications.length > 0) return;

    let alive = true;
    (async () => {
      setPublicationsLoading(true);
      setPublicationsError(null);
      try {
        const params = {
          current_page: 1,
          record_per_page: 30,
          ks: '',
          sub_ks: '',
          'dept_id[]': [deptId],
          order_by: 'date',
          order: 'desc',
          date_type: 'input_date',
          in_date_start: '',
          in_date_end: '',
        };
        const data = await get('/rm/v1/publications', { params });
        if (!alive) return;
        const records = data?.result?.records || data?.records || [];
        setPublications(Array.isArray(records) ? records : []);
      } catch (e) {
        if (!alive) return;
        setPublicationsError(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setPublicationsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tab, deptId, publications.length]);

  // 표시할 publications (기본 5개, More 버튼 클릭 시 전체)
  const displayedPublications = useMemo(() => {
    if (!Array.isArray(publications)) return [];
    return showAllPublications ? publications : publications.slice(0, 5);
  }, [publications, showAllPublications]);

  // More 버튼 URL 생성 (API 파라메터 기준)
  const morePublicationsUrl = useMemo(() => {
    if (!deptId) return '/publications/';
    const sp = new URLSearchParams();
    sp.append('dept_id[]', String(deptId));
    sp.set('order_by', 'date');
    sp.set('order', 'desc');
    sp.set('date_type', 'input_date');
    sp.set('current_page', '1');
    // sp.set('record_per_page', '30');
    const queryString = sp.toString();
    return `/publications/?${queryString}`;
  }, [deptId]);

  return (
    <div>
      {/* Tab Header */}
      <div className="researcher-cont-tabM tabM mt-[30px] mx-[-24px] text-[15px] font-medium text-[#8D9AAF] text-center border-b border-b-[#E4E8F1] overflow-x-auto sm:overflow-x-hidden lg:mx-[-60px] lg:mt-[52px]">
        <div className="tab-wrapper w-max sm:w-full">
          <div className={(isResearchers ? 'active ' : '') + 'sm:w-[50%] lg:hover:text-[#333]'}
            onClick={(e) => {
              try { e.preventDefault(); e.stopPropagation(); } catch { }
              setTab('researchers');
            }}
            style={{ cursor: "pointer" }}>
            <button
              type="button"
              className={tabBtnCls}
            >
              Researchers
            </button>
          </div>

          <div className={(isResources ? 'active ' : '') + 'sm:w-[50%] lg:hover:text-[#333]'}
            onClick={(e) => {
              try { e.preventDefault(); e.stopPropagation(); } catch { }
              setTab('resources');
            }}
            style={{ cursor: "pointer" }}
          >
            <button
              type="button"
              className={tabBtnCls}
            >
              Publications
            </button>
          </div>
        </div>
      </div>

      <div className="researcher-tabM-cont tabM-cont">
        {/* Researchers Tab */}
        <div className={`pt-[50px] lg:pt-[40px] ${tab === 'researchers' ? 'active' : 'hidden'}`}>
          <div className="mb-[25px] lg:mb-[30px]">
            <ul className="researcher-cate flex flex-wrap gap-2 w-full text-[14px] text-[#6C7687] leading-[27px]">
              <li
                className={`px-3 h-[27px] rounded-full cursor-pointer ${sinbunFilter === 'professor' ? 'active bg-[#376BDB] text-white' : 'bg-[#E4E8F1]'}`}
                onClick={() => handleSinbunFilter('professor')}
              >
                Professors
              </li>
              <li
                className={`px-3 h-[27px] rounded-full cursor-pointer ${sinbunFilter === 'graduate' ? 'active bg-[#376BDB] text-white' : 'bg-[#E4E8F1]'}`}
                onClick={() => handleSinbunFilter('graduate')}
              >
                Graduate Students
              </li>
              <li
                className={`px-3 h-[27px] rounded-full cursor-pointer ${sinbunFilter === 'more' ? 'active bg-[#376BDB] text-white' : 'bg-[#E4E8F1]'}`}
                onClick={() => handleSinbunFilter('more')}
              >
                More Scholars
              </li>
            </ul>
          </div>

          <div>
            <div className="w-full">
              <ul ref={scholarsListRef} className="w-full flex flex-col gap-3 md:flex-row md:flex-wrap md:gap-[30px]">
                {paginatedScholars.length > 0 ? (
                  paginatedScholars.map((scholar, index) => (
                    <ResearcherCard
                      key={scholar.scholar_id || scholar.authority_id || index}
                      item={{
                        ...scholar,
                        doc_count: scholar.resource_count,
                        scholarly_output: scholar.resource_count,
                      }}
                      showFavoriteButton={false}
                      liClassName="relative p-8 pb-[14px] bg-white border border-[#E4E8F1] rounded-xl md:w-[calc((100%-30px)/2)] xl:w-[calc((100%-60px)/3)] xl:px-[30px] xl:pt-[26px] xl:pb-4 hover:shadow-[0px_4px_20px_rgba(0,0,0,0.05)] tansition duration-300"
                    />
                  ))
                ) : (
                  <li className="w-full text-center text-[#6C7687] py-8">
                    No researchers found.
                  </li>
                )}
              </ul>
            </div>
          </div>

          {totalPages > 1 && (
            <Paginator pageInfo={pageInfo} onPage={onPage} />
          )}
        </div>

        {/* Resources Tab */}
        <div className={`${tab === 'resources' ? 'active' : 'hidden'} pt-[50px] lg:pt-[40px]`}>
          {publicationsLoading && (
            <div className="relative">
              <ChartSkeletonLoader show={publicationsLoading} />
            </div>
          )}
          {publicationsError && (
            <div className="text-center text-red-600 py-8">
              Error: {publicationsError}
            </div>
          )}
          {!publicationsLoading && !publicationsError && (
            <>
              <ul className="publication-list-wrap mt-4 flex flex-col gap-3 lg:mt-3">
                {displayedPublications.length > 0 ? (
                  displayedPublications.map((pub, index) => (
                    <PublicationCard
                      key={pub.publication_id || pub.resource_id || pub.id || index}
                      item={pub}
                      showAltmetric={true}
                    />
                  ))
                ) : (
                  <li className="w-full text-center text-[#6C7687] py-8">
                    No publications found.
                  </li>
                )}
              </ul>

              {publications.length > 5 && !showAllPublications && (
                <div className="text-center mt-10">
                  <a
                    href={morePublicationsUrl}
                    onClick={(e) => {
                      try { e.preventDefault(); e.stopPropagation(); } catch { }
                      try { navigate(morePublicationsUrl); } catch (err) { window.location.href = morePublicationsUrl; }
                    }}
                    className="inline-flex items-center justify-center gap-1 py-3 px-8 bg-[#F3F5F9] border border-[#E4E8F1] rounded-xl cursor-pointer hover:bg-[#E4E8F1] transition-colors"
                  >
                    <span className="text-[14px] text-[#6C7687]">More</span>
                    <img src={rmVars.themeUrl + 'images/launch-ico-6C7687.png'} className="w-[16px]" alt="link" />
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
